import type { ReactNode } from 'react';

/**
 * A tiny, dependency-free syntax highlighter for the languages Soteria
 * actually emits (Python, SQL, JSON, TS/JS, shell).
 *
 * Why hand-rolled: a real highlighter (shiki / prism) is a new npm dependency,
 * and adding one here means regenerating a lockfile that CI installs with a
 * different Node than any local machine — a known source of broken builds in
 * this repo. Flat, unhighlighted code in a quant product reads as unfinished,
 * so this covers the common cases and degrades to plain text for anything else.
 *
 * Deliberately NOT a parser. It is a single-pass, ordered-rule tokenizer:
 * comments and strings are matched before keywords, so a keyword inside a
 * string is never recolored. Unknown languages, and code over the size cap,
 * render as plain text.
 */

type TokenType =
  | 'plain'
  | 'comment'
  | 'string'
  | 'number'
  | 'keyword'
  | 'builtin';

interface Rule {
  type: TokenType;
  re: RegExp;
}

// Beyond this, skip highlighting entirely — the tokenizer is O(n) but the extra
// DOM nodes are not worth it for a huge dump.
const MAX_LENGTH = 20000;

const kw = (words: Array<string>): RegExp =>
  new RegExp(`\\b(?:${words.join('|')})\\b`, 'y');

const PYTHON: Array<Rule> = [
  { type: 'comment', re: /#[^\n]*/y },
  { type: 'string', re: /(?:[rbfu]|rb|br)?("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*')/y },
  { type: 'number', re: /\b\d[\d_]*(?:\.\d+)?(?:[eE][+-]?\d+)?\b/y },
  {
    type: 'keyword',
    re: kw([
      'and','as','assert','async','await','break','class','continue','def','del',
      'elif','else','except','finally','for','from','global','if','import','in',
      'is','lambda','nonlocal','not','or','pass','raise','return','try','while',
      'with','yield','None','True','False','match','case',
    ]),
  },
  {
    type: 'builtin',
    re: kw([
      'self','cls','print','len','range','dict','list','set','tuple','int','float',
      'str','bool','sum','min','max','abs','round','sorted','enumerate','zip',
      'isinstance','super','open','type',
    ]),
  },
];

const SQL: Array<Rule> = [
  { type: 'comment', re: /--[^\n]*|\/\*[\s\S]*?\*\//y },
  { type: 'string', re: /'(?:''|[^'])*'/y },
  { type: 'number', re: /\b\d+(?:\.\d+)?\b/y },
  {
    type: 'keyword',
    re: new RegExp(
      `\\b(?:${[
        'SELECT','FROM','WHERE','GROUP','BY','ORDER','HAVING','LIMIT','OFFSET',
        'JOIN','LEFT','RIGHT','INNER','OUTER','FULL','ON','AS','AND','OR','NOT',
        'IN','IS','NULL','LIKE','BETWEEN','CASE','WHEN','THEN','ELSE','END',
        'INSERT','INTO','VALUES','UPDATE','SET','DELETE','CREATE','TABLE','VIEW',
        'WITH','UNION','ALL','DISTINCT','ASC','DESC','OVER','PARTITION',
      ].join('|')})\\b`,
      'iy',
    ),
  },
  {
    type: 'builtin',
    re: new RegExp(
      `\\b(?:${[
        'count','sum','avg','min','max','argMax','argMin','coalesce','cast',
        'round','abs','now','toDate','toDateTime','any','uniq',
      ].join('|')})\\b`,
      'iy',
    ),
  },
];

const TS: Array<Rule> = [
  { type: 'comment', re: /\/\/[^\n]*|\/\*[\s\S]*?\*\//y },
  { type: 'string', re: /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'/y },
  { type: 'number', re: /\b\d[\d_]*(?:\.\d+)?(?:[eE][+-]?\d+)?\b/y },
  {
    type: 'keyword',
    re: kw([
      'as','async','await','break','case','catch','class','const','continue',
      'default','delete','do','else','enum','export','extends','finally','for',
      'from','function','if','implements','import','in','instanceof','interface',
      'let','new','of','return','satisfies','static','switch','this','throw',
      'try','type','typeof','var','void','while','yield','true','false','null',
      'undefined','readonly','public','private','protected',
    ]),
  },
  { type: 'builtin', re: kw(['console','window','document','Math','JSON','Object','Array','Promise','String','Number','Boolean']) },
];

const JSON_RULES: Array<Rule> = [
  { type: 'string', re: /"(?:\\.|[^"\\])*"/y },
  { type: 'number', re: /-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b/y },
  { type: 'keyword', re: kw(['true', 'false', 'null']) },
];

const BASH: Array<Rule> = [
  { type: 'comment', re: /#[^\n]*/y },
  { type: 'string', re: /"(?:\\.|[^"\\])*"|'[^']*'/y },
  { type: 'number', re: /\b\d+\b/y },
  {
    type: 'keyword',
    re: kw(['if','then','elif','else','fi','for','while','do','done','case','esac','in','function','return','export','local','source']),
  },
  { type: 'builtin', re: kw(['echo','cd','ls','cat','grep','sed','awk','npm','npx','git','docker','uv','python3','curl','mkdir','rm','cp','mv']) },
];

const BY_LANG: Record<string, Array<Rule>> = {
  python: PYTHON,
  py: PYTHON,
  sql: SQL,
  json: JSON_RULES,
  ts: TS,
  tsx: TS,
  typescript: TS,
  js: TS,
  jsx: TS,
  javascript: TS,
  bash: BASH,
  sh: BASH,
  shell: BASH,
  zsh: BASH,
};

// Code-specific tokens rather than the app palette: the block is a dark slab in
// both themes, and --primary / --success are chosen for contrast against white.
const CLASS: Record<TokenType, string> = {
  plain: '',
  comment: 'text-code-comment italic',
  string: 'text-code-string',
  number: 'text-code-number',
  keyword: 'text-code-keyword',
  builtin: 'text-code-builtin',
};

/** Single pass, ordered rules, sticky-anchored at the cursor. */
function tokenize(code: string, rules: Array<Rule>): Array<{ type: TokenType; value: string }> {
  const out: Array<{ type: TokenType; value: string }> = [];
  let plain = '';
  let i = 0;

  const flush = () => {
    if (plain) {
      out.push({ type: 'plain', value: plain });
      plain = '';
    }
  };

  while (i < code.length) {
    let matched = false;
    for (const rule of rules) {
      rule.re.lastIndex = i;
      const m = rule.re.exec(code);
      if (m && m.index === i && m[0].length > 0) {
        flush();
        out.push({ type: rule.type, value: m[0] });
        i += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      plain += code[i];
      i += 1;
    }
  }
  flush();
  return out;
}

/**
 * Returns highlighted nodes for `code`, or the plain string when the language
 * is unknown/unsupported or the input is too large.
 */
export function highlightCode(code: string, lang: string): ReactNode {
  const rules = BY_LANG[lang.toLowerCase()];
  if (!rules || code.length > MAX_LENGTH) return code;

  return tokenize(code, rules).map((t, i) =>
    t.type === 'plain' ? (
      t.value
    ) : (
      <span key={i} className={CLASS[t.type]}>
        {t.value}
      </span>
    ),
  );
}
